// Better: auto-size from SVG itself and keep aspect ratio
export async function svgContainerToPngBase64(containerEl, { scale = 2, inlineImages = false } = {}) {
  if (!containerEl) return null;

  const svgEl = containerEl.querySelector('svg');
  if (!svgEl) return null;

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  // Clone SVG so optional inlining never mutates the live node.
  const svgNodeForExport = svgEl.cloneNode(true);

  // Optional: inline external <image> assets (needed for graph backgrounds in some tools).
  if (inlineImages) {
    const imageNodes = Array.from(svgNodeForExport.querySelectorAll('image'));
    await Promise.all(
      imageNodes.map(async (node) => {
        const href = node.getAttribute('href') || node.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (!href || href.startsWith('data:')) return;
        try {
          const absoluteUrl = new URL(href, window.location.href).href;
          const response = await fetch(absoluteUrl);
          if (!response.ok) return;
          const blob = await response.blob();
          const dataUrl = await blobToDataUrl(blob);
          node.setAttribute('href', dataUrl);
          node.setAttributeNS('http://www.w3.org/1999/xlink', 'href', dataUrl);
        } catch (err) {
          // Best effort: leave original href when inlining fails.
        }
      })
    );
  }

  // --- Get intrinsic SVG dimensions ---
  let svgWidth;
  let svgHeight;

  if (svgEl.viewBox && svgEl.viewBox.baseVal && svgEl.viewBox.baseVal.width) {
    const vb = svgEl.viewBox.baseVal;
    svgWidth = vb.width;
    svgHeight = vb.height;
  } else {
    // Fallback to width/height attributes or rendered size
    svgWidth = (svgEl.width && svgEl.width.baseVal && svgEl.width.baseVal.value) || svgEl.clientWidth || 800;
    svgHeight = (svgEl.height && svgEl.height.baseVal && svgEl.height.baseVal.value) || svgEl.clientHeight || 400;
  }

  const canvasWidth = svgWidth * scale;
  const canvasHeight = svgHeight * scale;

  const svgData = new XMLSerializer().serializeToString(svgNodeForExport);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = new Image();
    img.src = url;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw SVG scaled uniformly
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl.split(',')[1];
  } finally {
    URL.revokeObjectURL(url);
  }
}
